/**
 * Формат query string адресов выдачи. Его знает только этот модуль.
 *
 * - Массивы пишутся с индексами: `metro[0]=4&metro[1]=7`.
 * - При разборе числа и `true`/`false` становятся числами и булевыми значениями,
 *   ключ без `=` – `null`.
 * - При сборке ключи сортируются, массивы нумеруются заново с нуля, `!'()*` кодируются
 *   (RFC 3986), `undefined` пропускается, `null` пишется одним ключом.
 *
 * Формат повторяет пакет `query-string` с опциями `{ arrayFormat: 'index', parseNumbers: true,
 * parseBooleans: true }`, которым сервис пользовался раньше, чтобы адреса не изменились.
 * Пакет заменён этим модулем: версия 9 требует `String.prototype.replaceAll` (нет в Chrome < 85
 * и iOS < 13.4), а версии до 9.5 тянут уязвимый `decode-uri-component`.
 * Декодирование идёт через `URLSearchParams`: оно не бросает исключений на битых
 * `%`-последовательностях и работает за линейное время на любом входе. Отличия от `query-string`:
 * - байты, которые не складываются в UTF-8 (например, `%C1` из ссылки в cp1251), становятся
 *   символом U+FFFD, а не остаются в значении как есть;
 * - ключ, у которого обычное значение идёт раньше индексного (`zoom=1&zoom[0]=2`), сохраняет
 *   обычное значение, а `query-string` на таком адресе бросал TypeError;
 * - ключ `__proto__` сохраняется, а `query-string` при сборке его терял.
 */

export type TQueryValue = string | number | boolean | null;
export type TQuery = Record<string, TQueryValue | TQueryValue[] | undefined>;

export interface IUrlWithQuery {
  /** Адрес без query string и фрагмента */
  url: string;
  query: TQuery;
}

const INDEXED_KEY_REGEX = /\[(\d*)]$/;

export function parseQuery(queryString: string): TQuery {
  const plainValues = new Map<string, string | null>();
  const indexedValues = new Map<string, Map<string, string | null>>();
  const trimmed = queryString.trim().replace(/^[?#&]/, '');

  for (const parameter of trimmed.split('&')) {
    if (!parameter) {
      continue;
    }

    const separatorIndex = parameter.indexOf('=');
    const rawKey = separatorIndex === -1 ? parameter : parameter.slice(0, separatorIndex);
    const value = separatorIndex === -1 ? null : decodeComponent(parameter.slice(separatorIndex + 1));
    const key = decodeComponent(rawKey);
    const indexMatch = INDEXED_KEY_REGEX.exec(key);

    if (!indexMatch) {
      plainValues.set(key, value);
      indexedValues.delete(key);
      continue;
    }

    const baseKey = key.replace(INDEXED_KEY_REGEX, '');
    if (plainValues.has(baseKey)) {
      // Обычное значение у ключа уже есть: индексные значения к нему не добавляем
      continue;
    }

    const items = indexedValues.get(baseKey) ?? new Map<string, string | null>();
    items.set(indexMatch[1], value);
    indexedValues.set(baseKey, items);
  }

  // Объект без прототипа: ключи `__proto__` и `constructor` из адреса остаются обычными ключами
  const query: TQuery = Object.create(null);
  const keys = [...plainValues.keys(), ...indexedValues.keys()].sort();

  for (const key of keys) {
    const items = indexedValues.get(key);

    query[key] = items ? toSortedArray(items).map(parseValue) : parseValue(plainValues.get(key) ?? null);
  }

  return query;
}

export function stringifyQuery(query: TQuery): string {
  return Object.keys(query)
    .sort()
    .map(key => stringifyEntry(key, query[key]))
    .filter(part => part.length > 0)
    .join('&');
}

export function parseUrl(url: string): IUrlWithQuery {
  const withoutHash = url.split('#')[0];
  const queryStart = withoutHash.indexOf('?');

  if (queryStart === -1) {
    return { url: withoutHash, query: {} };
  }

  return {
    url: withoutHash.slice(0, queryStart),
    query: parseQuery(withoutHash.slice(queryStart + 1)),
  };
}

export function stringifyUrl({ url, query }: IUrlWithQuery): string {
  const queryString = stringifyQuery(query);

  return queryString ? `${url}?${queryString}` : url;
}

function decodeComponent(value: string): string {
  return new URLSearchParams(`v=${value}`).get('v') ?? '';
}

function encodeComponent(value: string | number | boolean): string {
  return encodeURIComponent(value).replace(/[!'()*]/g, char => `%${char.charCodeAt(0).toString(16).toUpperCase()}`);
}

function toSortedArray(items: Map<string, string | null>): (string | null)[] {
  return [...items.keys()]
    .sort()
    .sort((a, b) => Number(a) - Number(b))
    .map(index => items.get(index) ?? null);
}

function parseValue(value: string | null): TQueryValue {
  if (value === null) {
    return null;
  }

  const lowerCased = value.toLowerCase();
  if (lowerCased === 'true' || lowerCased === 'false') {
    return lowerCased === 'true';
  }

  if (value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value);
  }

  return value;
}

function stringifyEntry(key: string, value: TQuery[string]): string {
  if (value === undefined) {
    return '';
  }

  if (value === null) {
    return encodeComponent(key);
  }

  if (Array.isArray(value)) {
    const parts: string[] = [];

    for (const item of value) {
      if (item === undefined) {
        continue;
      }

      const index = parts.length;
      parts.push(
        item === null
          ? `${encodeComponent(key)}[${index}]`
          : `${encodeComponent(key)}[${index}]=${encodeComponent(item)}`,
      );
    }

    return parts.join('&');
  }

  return `${encodeComponent(key)}=${encodeComponent(value)}`;
}
